import { msg } from '@gonfalon/messages/msg';
import type { schemas } from '@gonfalon/openapi/types';
import { formatInTimeZone } from 'date-fns-tz';

import {
  AggregationType,
  type Granularity,
  type SeriesData,
  type UsageSeriesList,
  type UsageTimestamps,
} from '../../types';
import type { UsageTooltipComponent } from '../components/charts/UsageDetailChart/getTooltipFormatter';
import { carryForwardCumulativeSeriesValues, getNormalizedSeries } from '../components/charts/UsageDetailChart/utils';

import { getSeriesTotal } from './getSeriesTotal';
import { hasDataExportUsage } from './hasDataExportUsage';

export { hasDataExportUsage };

export enum DataExportScope {
  BOTH = 'both',
  WAREHOUSE = 'warehouse',
  STREAMING = 'streaming',
}

export type DataExportSource = DataExportScope.STREAMING | DataExportScope.WAREHOUSE;

export type DataExportSourceState = {
  data?: UsageSeriesList;
  isLoading: boolean;
  isError: boolean;
  isUnsupportedGroupBy?: boolean;
};

export type DataExportSourceStates = Record<DataExportSource, DataExportSourceState>;

type AlignedDataExportSeries = Record<DataExportSource | 'combined', UsageSeriesList>;

function toSeriesList(points: SeriesData): UsageSeriesList {
  return { _links: {}, metadata: [], series: points.map(({ time, value }) => ({ time, '0': value ?? 0 })) };
}

function toSeriesData(series: UsageSeriesList['series'] | undefined, times: number[]): SeriesData {
  const byTime = new Map((series ?? []).map((slice) => [slice.time, slice['0'] ?? 0]));
  return times.map((time) => ({ time, value: byTime.get(time) ?? 0 }));
}

export function alignDataExportSeries({
  streaming,
  warehouse,
  aggregationType,
}: {
  streaming?: UsageSeriesList;
  warehouse?: UsageSeriesList;
  aggregationType: AggregationType;
}): AlignedDataExportSeries {
  const times = Array.from(
    new Set([...(streaming?.series ?? []), ...(warehouse?.series ?? [])].map(({ time }) => time)),
  ).sort((left, right) => left - right);

  const align = (series?: UsageSeriesList) => {
    const points = toSeriesData(series?.series, times);
    return aggregationType === AggregationType.MONTH_TO_DATE ? carryForwardCumulativeSeriesValues(points) : points;
  };
  const streamingPoints = align(streaming);
  const warehousePoints = align(warehouse);
  const combinedPoints = times.map((time, i) => ({
    time,
    value: (streamingPoints[i].value ?? 0) + (warehousePoints[i].value ?? 0),
  }));

  return {
    [DataExportScope.STREAMING]: toSeriesList(streamingPoints),
    [DataExportScope.WAREHOUSE]: toSeriesList(warehousePoints),
    combined: toSeriesList(combinedPoints),
  };
}

const DATA_EXPORT_SOURCES: DataExportSource[] = [DataExportScope.STREAMING, DataExportScope.WAREHOUSE];

export type DataExportSourceCapabilities = schemas['DataExportSourceCapabilities'];

export type DataExportCapabilitiesState = {
  data?: DataExportSourceCapabilities;
  isLoading: boolean;
  isError?: boolean;
};

export function usesDataExportSource(source: DataExportSource, ...sourceSets: DataExportSourceStates[]): boolean {
  const counts = ({ data, isLoading, isError }: DataExportSourceState) =>
    !isLoading && (isError || hasDataExportUsage(data));
  return sourceSets.some((sources) => counts(sources[source]));
}

export function hasDataExportSource(
  source: DataExportSource,
  capabilities: DataExportCapabilitiesState | undefined,
  ...sourceSets: DataExportSourceStates[]
): boolean {
  return capabilities?.data?.[source].enabled === true || usesDataExportSource(source, ...sourceSets);
}

export function hasBothDataExportSources(
  capabilities: DataExportCapabilitiesState | undefined,
  ...sourceSets: DataExportSourceStates[]
): boolean {
  return DATA_EXPORT_SOURCES.every((source) => hasDataExportSource(source, capabilities, ...sourceSets));
}

export function getEffectiveDataExportScope(
  scope: DataExportScope,
  capabilities: DataExportCapabilitiesState | undefined,
  ...sourceSets: DataExportSourceStates[]
): DataExportScope {
  const present = DATA_EXPORT_SOURCES.filter((source) => hasDataExportSource(source, capabilities, ...sourceSets));
  if (present.length === DATA_EXPORT_SOURCES.length) {
    return scope;
  }
  const isSettled =
    !capabilities?.isLoading &&
    sourceSets.every((sources) => DATA_EXPORT_SOURCES.every((source) => !sources[source].isLoading));
  return isSettled && present.length === 1 ? present[0] : DataExportScope.BOTH;
}

export type DataExportChartView =
  | { status: 'loading' }
  | { status: 'error'; failedSources: DataExportSource[] }
  | {
      status: 'ready';
      data: UsageSeriesList;
      total: number;
      tooltipComponents: UsageTooltipComponent[];
      showLimit: boolean;
    };

const TOOLTIP_SOURCES: Array<{ source: DataExportSource; label: () => string }> = [
  { source: DataExportScope.WAREHOUSE, label: () => msg('billing.dataExportCombined.warehouse') },
  { source: DataExportScope.STREAMING, label: () => msg('billing.dataExportCombined.streaming') },
];

export function getScopeSources(scope: DataExportScope): DataExportSource[] {
  return scope === DataExportScope.BOTH ? [DataExportScope.STREAMING, DataExportScope.WAREHOUSE] : [scope];
}

export function getDataExportChartView({
  sources,
  scope,
  aggregationType,
  granularity,
  timestamps,
  hasBothSources = hasBothDataExportSources(undefined, sources),
}: {
  sources: DataExportSourceStates;
  scope: DataExportScope;
  aggregationType: AggregationType;
  granularity: Granularity;
  timestamps: UsageTimestamps;
  hasBothSources?: boolean;
}): DataExportChartView {
  const inScope = getScopeSources(scope);
  const failedSources = inScope.filter((source) => sources[source].isError);
  if (failedSources.length > 0) {
    return { status: 'error', failedSources };
  }
  if (inScope.some((source) => sources[source].isLoading || !sources[source].data)) {
    return { status: 'loading' };
  }

  const aligned = alignDataExportSeries({
    streaming: sources[DataExportScope.STREAMING].data,
    warehouse: sources[DataExportScope.WAREHOUSE].data,
    aggregationType,
  });
  const data = scope === DataExportScope.BOTH ? aligned.combined : aligned[scope];
  const normalize = (series: UsageSeriesList) =>
    getNormalizedSeries(
      series.series,
      timestamps.from,
      timestamps.to,
      timestamps.cutoff,
      aggregationType,
      0,
      granularity,
    );

  return {
    status: 'ready',
    data,
    total: getSeriesTotal({ series: data.series, aggregationType, granularity }),
    tooltipComponents: !hasBothSources
      ? []
      : TOOLTIP_SOURCES.filter(({ source }) => inScope.includes(source)).map(({ source, label }) => ({
          label: label(),
          series: normalize(aligned[source]),
          isIncluded: true,
        })),
    showLimit: scope === DataExportScope.BOTH || !hasBothSources,
  };
}

export type DataExportFreshness = {
  dataThrough: number;
  delayedSources: DataExportSource[];
};

const RFC_3339_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

function parseDataThrough(value: UsageSeriesList['dataThrough']): number | undefined {
  const time = value !== undefined && RFC_3339_UTC.test(value) ? Date.parse(value) : NaN;
  return Number.isFinite(time) ? time : undefined;
}

export function getDataExportFreshness(
  sources: DataExportSourceStates,
  scope: DataExportScope,
): DataExportFreshness | undefined {
  const inScope = getScopeSources(scope);
  const dataThrough = inScope
    .map((source) => parseDataThrough(sources[source].data?.dataThrough))
    .filter((time): time is number => time !== undefined);
  if (dataThrough.length === 0) {
    return undefined;
  }
  return {
    dataThrough: Math.min(...dataThrough),
    delayedSources: inScope.filter((source) => sources[source].data?.delayed === true),
  };
}

export function formatDataExportDataThrough(dataThrough: number, today: Date): string {
  const date = new Date(dataThrough);
  const isToday =
    date.getUTCFullYear() === today.getUTCFullYear() &&
    date.getUTCMonth() === today.getUTCMonth() &&
    date.getUTCDate() === today.getUTCDate();
  return formatInTimeZone(date, 'UTC', isToday ? 'HH:mm' : 'MMM d, HH:mm');
}
